import React, { useState, useRef } from "react";
import { AvatarChip } from "./AvatarChip";
import { TimezoneSelect } from "./TimezoneSelect";
import { RoleOpeningsEditor } from "./RoleOpeningsEditor";
import { updateTeam } from "../lib/api";

const DAYS = [
  { code: "mon", label: "MON" },
  { code: "tue", label: "TUE" },
  { code: "wed", label: "WED" },
  { code: "thu", label: "THU" },
  { code: "fri", label: "FRI" },
  { code: "sat", label: "SAT" },
  { code: "sun", label: "SUN" },
];

const SLOTS = [
  { code: "morning", label: "Morning", hours: "06–12" },
  { code: "afternoon", label: "Afternoon", hours: "12–18" },
  { code: "evening", label: "Evening", hours: "18–22" },
  { code: "night", label: "Night", hours: "22–06" },
];

const DEFAULT_TZ = "UTC";

export function TeamEditModal({ team, onClose, onSaved, onToast }) {
  const [name, setName] = useState(team.name || "");
  const [avatarId, setAvatarId] = useState(team.avatar_id ?? 0);
  const [pfpUrl, setPfpUrl] = useState(team.pfp_url || null);
  const [socialLink, setSocialLink] = useState(team.social_link || "");
  const [slots, setSlots] = useState(team.availability?.slots || {});
  const [tz, setTz] = useState(team.availability?.timezone || DEFAULT_TZ);
  const [isOpen, setIsOpen] = useState(Boolean(team.is_open));
  const [isBeginner, setIsBeginner] = useState(Boolean(team.is_beginner));
  const [maxMembers, setMaxMembers] = useState(Number.isFinite(team.max_members) ? team.max_members : 8);
  const [openings, setOpenings] = useState(Array.isArray(team.openings) ? team.openings : []);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const toggleSlot = (day, slot) => {
    setSlots((prev) => {
      const cur = new Set(prev[day] || []);
      if (cur.has(slot)) cur.delete(slot); else cur.add(slot);
      return { ...prev, [day]: Array.from(cur) };
    });
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) {
      onToast?.("Image too large (max 1.5 MB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPfpUrl(reader.result);
      setAvatarId(null);
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      onToast?.("Team name required.");
      return;
    }
    setBusy(true);
    try {
      const patch = {
        name: name.trim(),
        social_link: socialLink.trim(),
        availability: { slots, timezone: tz },
        is_open: isOpen,
        is_beginner: isBeginner,
        max_members: Math.max(1, Math.min(50, Number(maxMembers) || 8)),
        openings,
      };
      if (pfpUrl) {
        patch.pfp_url = pfpUrl;
      } else {
        patch.avatar_id = avatarId ?? 0;
      }
      const updated = await updateTeam(team.team_id, patch);
      onToast?.(`✅ ${updated.name} updated.`);
      onSaved?.();
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Could not save changes.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[97] bg-black/85 backdrop-blur-md p-4 flex items-center justify-center"
      onClick={onClose}
      data-testid="team-edit-backdrop"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="glass rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto neon-glow p-6 space-y-5"
        data-testid="team-edit-modal"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
              <i className="fas fa-crown mr-1" />MANAGER · EDIT TEAM
            </p>
            <h2 className="font-display text-2xl grad-text mt-1">{team.name}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="team-edit-close-btn"
            className="text-white/60 hover:text-white text-2xl"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Identity */}
        <div className="space-y-3">
          <label className="block">
            <span className="text-[10px] tracking-[3px] text-[#a3a3ac]">TEAM NAME</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="team-edit-name"
              className="mt-1 w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white"
            />
          </label>

          <div>
            <span className="text-[10px] tracking-[3px] text-[#a3a3ac]">TEAM CREST</span>
            <div className="flex items-center gap-3 mt-1">
              <AvatarChip avatarId={avatarId} pfpUrl={pfpUrl} size={64} />
              <div className="flex-1 grid grid-cols-6 gap-2">
                {Array.from({ length: 12 }).map((_, i) => (
                  <AvatarChip
                    key={i}
                    avatarId={i}
                    size={36}
                    selected={avatarId === i && !pfpUrl}
                    onClick={() => { setAvatarId(i); setPfpUrl(null); }}
                    dataTestid={`team-avatar-option-${i}`}
                  />
                ))}
              </div>
            </div>
            <input
              type="file"
              accept="image/*"
              ref={fileRef}
              onChange={handleFile}
              data-testid="team-edit-upload-input"
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="team-edit-upload-btn"
              className="mt-2 text-xs tracking-widest px-3 py-1.5 rounded-lg bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25"
            >
              <i className="fas fa-upload mr-1" />UPLOAD CUSTOM CREST (max 1.5MB)
            </button>
            {pfpUrl && (
              <button
                type="button"
                onClick={() => { setPfpUrl(null); setAvatarId(avatarId ?? 0); }}
                data-testid="team-edit-clear-pfp"
                className="ml-2 text-[10px] text-white/50 hover:text-white"
              >
                clear upload
              </button>
            )}
          </div>

          <label className="block">
            <span className="text-[10px] tracking-[3px] text-[#a3a3ac]">
              <i className="fas fa-link mr-1" />SOCIAL LINK <span className="text-white/40">— Discord, Twitter/X, Twitch, etc.</span>
            </span>
            <input
              type="text"
              value={socialLink}
              onChange={(e) => setSocialLink(e.target.value)}
              data-testid="team-edit-social"
              placeholder="discord.gg/yourteam"
              className="mt-1 w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white"
            />
          </label>
        </div>

        {/* Recruitment & cap — who can join + roster size */}
        <div
          data-testid="team-edit-recruitment"
          className="space-y-3 border-t border-[#14b8a6]/20 pt-4"
        >
          <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
            <i className="fas fa-door-open mr-1" />RECRUITMENT &amp; CAP
          </p>
          <p className="text-[11px] text-white/55 -mt-1">
            Open teams let any league member walk in (up to the cap). Beginner-friendly is a cosmetic flag for new players scouting their first team.
          </p>

          <div className="grid sm:grid-cols-2 gap-3">
            <label
              className={`flex items-start gap-3 rounded-2xl border p-3 cursor-pointer transition ${
                isOpen
                  ? "border-[#14b8a6]/60 bg-[#14b8a6]/10"
                  : "border-white/15 bg-black/40 hover:border-white/30"
              }`}
            >
              <input
                type="checkbox"
                checked={isOpen}
                onChange={(e) => setIsOpen(e.target.checked)}
                data-testid="team-edit-is-open"
                className="mt-0.5 accent-[#14b8a6]"
              />
              <span>
                <span className="block text-xs font-bold text-white">🟢 OPEN TEAM</span>
                <span className="block text-[10px] text-white/55 mt-0.5">
                  Any league member can join without an invite (until cap hits).
                </span>
              </span>
            </label>

            <label
              className={`flex items-start gap-3 rounded-2xl border p-3 cursor-pointer transition ${
                isBeginner
                  ? "border-[#14b8a6]/60 bg-[#14b8a6]/10"
                  : "border-white/15 bg-black/40 hover:border-white/30"
              }`}
            >
              <input
                type="checkbox"
                checked={isBeginner}
                onChange={(e) => setIsBeginner(e.target.checked)}
                data-testid="team-edit-is-beginner"
                className="mt-0.5 accent-[#14b8a6]"
              />
              <span>
                <span className="block text-xs font-bold text-white">🌱 BEGINNER-FRIENDLY</span>
                <span className="block text-[10px] text-white/55 mt-0.5">
                  Shown as a welcoming badge on your team card.
                </span>
              </span>
            </label>
          </div>

          <label className="flex items-center justify-between gap-3 rounded-2xl border border-white/15 bg-black/40 p-3">
            <span className="text-[10px] tracking-[3px] text-[#a3a3ac]">
              <i className="fas fa-users mr-1" />MAX MEMBERS
              <span className="block text-[10px] text-white/45 normal-case tracking-normal mt-0.5">
                1–50. Default 8.
              </span>
            </span>
            <input
              type="number"
              min={1}
              max={50}
              value={maxMembers}
              onChange={(e) => setMaxMembers(e.target.value)}
              data-testid="team-edit-max-members"
              className="bg-black/40 border border-white/15 rounded-lg px-3 py-2 w-20 text-white text-right"
            />
          </label>

          {/* Phase 36 — role openings */}
          <div className="border-t border-white/10 pt-3 mt-2">
            <RoleOpeningsEditor
              value={openings}
              onChange={setOpenings}
              game={team.supported_games?.[0]}
              testidPrefix="team-edit-openings"
            />
          </div>
        </div>

        {/* Availability — universal across all leagues */}
        <div data-testid="team-edit-availability" className="space-y-2 border-t border-[#14b8a6]/20 pt-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
              <i className="fas fa-calendar-check mr-1" />WEEKLY AVAILABILITY
            </p>
            <label className="flex items-center gap-2 text-[10px] text-white/60">
              <span>TZ</span>
              <TimezoneSelect
                value={tz}
                onChange={setTz}
                dataTestid="team-edit-timezone"
                className="bg-black/40 border border-white/15 rounded px-2 py-1 text-white text-[10px] max-w-[180px]"
              />
              </label>
            </div>
            <p className="text-[11px] text-white/55">Tick the slots when your team is around — opponents see this when scheduling matches.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[9px] tracking-widest text-white/40">
                    <th className="text-left py-1 pr-2">SLOT</th>
                    {DAYS.map((d) => (
                      <th key={d.code} className="text-center px-1">{d.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SLOTS.map((s) => (
                    <tr key={s.code} className="border-t border-white/5">
                      <td className="py-1.5 pr-2">
                        <span className="font-bold">{s.label}</span>
                        <span className="block text-[9px] text-white/40">{s.hours}</span>
                      </td>
                      {DAYS.map((d) => {
                        const checked = (slots[d.code] || []).includes(s.code);
                        return (
                          <td key={d.code} className="text-center px-1">
                            <button
                              type="button"
                              onClick={() => toggleSlot(d.code, s.code)}
                              data-testid={`team-edit-slot-${d.code}-${s.code}`}
                              aria-pressed={checked}
                              className={`w-7 h-7 rounded-md border transition ${
                                checked
                                  ? "bg-[#14b8a6]/25 border-[#14b8a6] text-[#14b8a6]"
                                  : "bg-black/40 border-white/15 text-white/30 hover:border-[#14b8a6]/50"
                              }`}
                            >
                              {checked ? "✓" : ""}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        <div className="flex gap-2 pt-2 border-t border-white/10">
          <button
            type="button"
            onClick={onClose}
            data-testid="team-edit-cancel"
            className="flex-1 btn-ghost rounded-2xl py-3"
          >
            CANCEL
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="team-edit-save"
            className="flex-1 btn-neon rounded-2xl py-3 disabled:opacity-40"
          >
            {busy ? "SAVING..." : "💾 SAVE TEAM"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default TeamEditModal;
